import {GridColDef} from '@mui/x-data-grid';
import {IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {Delete, Send} from '@mui/icons-material';
import {FormattedMessage} from 'react-intl';
import dayjs from 'dayjs';
import {CompanyCredentialDTO} from 'src/types/company';
import {CredentialStatus} from './CredentialStatus/CredentialStatus';
import {
    actionsButtonSx,
    actionsIconSx,
    actionsRootSx,
    mutedDashSx,
    nameCellSx,
} from './CompanyCredentialsTable.sx';

/**
 * The holder's name, built in one place because both halves are optional.
 *
 * A credential is created from an email address alone, so a row may have no name at all until the
 * person is named - the card and the grid cell must then show the same thing.
 */
export const credentialName = (credential: CompanyCredentialDTO): string =>
    `${credential.firstName ?? ''} ${credential.lastName ?? ''}`.trim();

export const columnConfigCompanyCredentials = (
    onResend: (credentialId: number) => void,
    onDelete: (credentialId: number) => void,
    /** Without the write right the column is absent rather than empty, so the grid keeps its width. */
    canManage: boolean
): GridColDef<CompanyCredentialDTO>[] => {
    const columns: GridColDef<CompanyCredentialDTO>[] = [
        {
            field: 'emailAddress',
            renderHeader: () => <FormattedMessage id="email"/>,
            flex: 2,
            minWidth: 180,
            sortable: true,
            cellClassName: 'ampTableCell',
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.emailAddress}
                </Typography>
            ),
        },
        {
            field: 'name',
            renderHeader: () => <FormattedMessage id="name"/>,
            flex: 1.5,
            minWidth: 140,
            sortable: true,
            cellClassName: 'ampTableCell',
            valueGetter: (params) => credentialName(params.row),
            renderCell: (params) => {
                const name = credentialName(params.row);

                return name ? (
                    <Typography
                        variant="body2"
                        sx={nameCellSx}
                    >
                        {name}
                    </Typography>
                ) : (
                    <Typography
                        variant="body2"
                        sx={mutedDashSx}
                    >
                        —
                    </Typography>
                );
            },
        },
        {
            field: 'status',
            renderHeader: () => <FormattedMessage id="status"/>,
            flex: 1,
            minWidth: 130,
            sortable: false,
            cellClassName: 'ampTableCell',
            renderCell: (params) => (
                <CredentialStatus passwordSet={!!params.row.passwordSetAt}/>
            ),
        },
        {
            field: 'passwordSetAt',
            renderHeader: () => <FormattedMessage id="passwordSetAt"/>,
            flex: 1,
            minWidth: 130,
            sortable: true,
            cellClassName: 'ampTableCell',
            // Epoch milliseconds on the wire, like every other date the platform sends.
            renderCell: (params) => (params.row.passwordSetAt ? (
                <Typography variant="body2">
                    {dayjs(params.row.passwordSetAt).format('DD/MM/YY')}
                </Typography>
            ) : (
                <Typography
                    variant="body2"
                    sx={mutedDashSx}
                >
                    —
                </Typography>
            )),
        },
        {
            field: 'lastLogin',
            renderHeader: () => <FormattedMessage id="lastLogin"/>,
            flex: 1,
            minWidth: 130,
            sortable: true,
            cellClassName: 'ampTableCell',
            // Null until the credential has actually been used, which is a different question from
            // whether a password was set: an invitation can be completed and then never signed in with.
            renderCell: (params) => (params.row.lastLogin ? (
                <Typography variant="body2">
                    {dayjs(params.row.lastLogin).format('DD/MM/YY')}
                </Typography>
            ) : (
                <Typography
                    variant="body2"
                    sx={mutedDashSx}
                >
                    —
                </Typography>
            )),
        },
        {
            field: 'actions',
            renderHeader: () => '',
            width: 96,
            sortable: false,
            cellClassName: 'ampTableCell',
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderCell: (params) => (
                <Stack sx={actionsRootSx}>
                    <Tooltip
                        title={<FormattedMessage id="resendInvitation"/>}
                        arrow
                    >
                        <IconButton
                            sx={actionsButtonSx}
                            onClick={() => onResend(params.row.id)}
                        >
                            <Send sx={actionsIconSx}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={<FormattedMessage id="delete"/>}
                        arrow
                    >
                        <IconButton
                            sx={actionsButtonSx}
                            onClick={() => onDelete(params.row.id)}
                        >
                            <Delete sx={actionsIconSx}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    return canManage ? columns : columns.filter((column) => column.field !== 'actions');
};
